import {
    ComponentFactory,
    ComponentFactoryResolver,
    ComponentRef,
    Injectable,
    Injector,
    ReflectiveInjector,
    ResolvedReflectiveProvider
} from "@angular/core";
import {Platform} from "ionic-angular";
import {ResponsiveService} from "./responsive.service";
import {Subject} from "rxjs";
import {Type} from "@angular/core/src/type";
import {Page, PageGeometry, PageSpec} from "./page";
import {OrchestrationEvents} from "./orchestration-events";
import {View} from "./view";
import {NavigationParams} from "./navigation-params";
import {SessionController} from "./session.controller";

/**
 * Manages navigation including full-pages, modals, views, and back-button handling.
 */
@Injectable()
export class NavigationService {
    public pageCreated$: Subject<PageSpec> = new Subject<PageSpec>();
    public pageDismissed$: Subject<ComponentRef<any>> = new Subject<ComponentRef<any>>();

    private componentFactories: { [selector: string]: ComponentFactory<any> } = {};
    private views: View[] = [];
    private exiting: boolean = false;
    private exited: boolean = false;

    // Avoid injection dependency on SessionController
    private _sessionController: SessionController;

    constructor(private injector: Injector,
                private responsiveService: ResponsiveService,
                private componentFactoryResolver: ComponentFactoryResolver,
                private platform: Platform) {
        console.log("--- NavigationService");

        // Listen for browser back button
        platform.registerBackButtonAction(() => this.dismissView());

        if (history.state === "exited") {
            history.replaceState("active", "");
        } else if (history.state !== "active") {
            history.pushState("root", "");
            history.pushState("active", "");
        }

        if (!this.responsiveService.isApp) {
            window.addEventListener("popstate", () => {
                if (this.exiting) {
                    return;
                }

                if (this.exited) {
                    history.pushState("exited", "");
                    return;
                }

                // Keep the "active" history state pushed while we still have view components on the stack.
                if (this.views.length) {
                    console.log("popstate: re-pushing active state");
                    history.pushState("active", "");
                    // noinspection JSIgnoredPromiseFromCall
                    this.dismissView();
                } else if (history.length > 3) {
                    console.log("popstate: allowing exit to previous page");
                    this.exiting = true;
                    history.back();
                    history.back();
                } else {
                    console.log("popstate: exiting to exit page");
                    this.exited = true;
                    history.pushState("exited", "");
                    // noinspection JSIgnoredPromiseFromCall
                    this.createPage("woa-exit-page", undefined, undefined, true);
                }
            });
        }
    }

    set sessionController(sessionController: SessionController) {
        this._sessionController = sessionController;
    }

    public registerComponents(componentTypes: Type<any>[]): void {
        componentTypes.forEach((componentType: Type<any>) => {
            const componentFactory: ComponentFactory<any> = this.componentFactoryResolver.resolveComponentFactory(componentType);
            this.componentFactories[componentFactory.selector] = componentFactory;
        });
    }

    public getComponentFactory(selector: string): ComponentFactory<any> {
        const componentFactory: ComponentFactory<Page> = this.componentFactories[selector];
        if (!componentFactory) {
            console.log("No component factory found for selector '" + selector + "'. Check that it has been added to entryComponents.");
            return undefined;
        }
        return componentFactory;
    }

    public redirectToUrl(url: string): void {
        OrchestrationEvents.redirecting$.next(true);
        window.location.href = url;
    }

    public exitApp(): void {
        this.views = [];
        history.back();
    }

    public createView(name: string): Promise<void> {
        const view: View = new View();
        view.viewName = name;
        this.registerView(view);

        // Promise is resolved on view dismissal
        return view.dismissal;
    }

    public async createPage(selector, params?: any, geometry?: PageGeometry, asRoot: boolean = false, forceIosFocus: boolean = false): Promise<any> {

        // Destroy all pages if setting a new root - normal dismissal lifecycle and transitions do not apply
        if (asRoot) {
            while (this.views.length) {
                const top: View = this.views[this.views.length - 1];
                if (top instanceof Page) {
                    top.componentRef.destroy();
                    top.containerComponentRef.destroy();
                }
                this.deregisterView(top);
            }
        }

        // Create the page component
        const providers: ResolvedReflectiveProvider[] = ReflectiveInjector.resolve([{
            provide: NavigationParams,
            useValue: new NavigationParams(params)
        }]);
        const injector: ReflectiveInjector = ReflectiveInjector.fromResolvedProviders(providers, this.injector);
        const componentFactory: ComponentFactory<Page> = this.getComponentFactory(selector);
        if (!componentFactory) {
            return undefined;
        }
        const componentRef: ComponentRef<Page> = componentFactory.create(injector);
        const page: Page = componentRef.instance;
        page.viewName = page.constructor.name;
        page.componentRef = componentRef;

        const activePage: Page = this.activePage;
        if (activePage) {
            activePage.onInactive();
        }

        // Register the page component as a view
        this.registerView(page);

        // Workaround for iOS+Safari behaviour where the keyboard doesn't open on setting focus automatically to a field in the page,
        // since it does not recognise that user interaction took place before the open transition.
        let input: HTMLInputElement;
        if (forceIosFocus && this.responsiveService.isIos) {
            input = document.createElement("input");
            input.style.width = "0";
            input.style.height = "0";
            input.style.margin = "0";
            input.style.padding = "0";
            input.style.border = "0";
            input.style.opacity = "0";
            document.body.appendChild(input);
            input.focus();
        }

        // Display the view
        this.pageCreated$.next(new PageSpec(componentRef, geometry));

        componentRef.instance.onActive();

        // Promise is resolved on page dismissal
        const result: any = await page.dismissal;
        if (input) {
            document.body.removeChild(input);
        }
        return result;
    }

    public canDismissView(): boolean {
        return this.views.length > 1;
    }

    public async dismissView(): Promise<any> {

        // If we have nothing to go back to, ask the session controller to exit
        if (this.views.length <= 1) {
            // noinspection ES6MissingAwait
            this._sessionController.confirmExitApp();
            return;
        }

        const activeView: View = this.views[this.views.length - 1];
        if (activeView instanceof Page) {
            return this.dismissPage(activeView);
        } else {
            this.deregisterView(activeView);
            activeView.viewPromiseResolve();
        }
    }

    public async dismissPage(page: Page, results?: any, failure: boolean = false): Promise<boolean> {
        for (let i: number = this.views.length - 1; i >= 0; i--) {
            if (this.views[i] === page) {

                // Allow the page component to prevent the dismissal (asynchronously)
                if (!await page.onWillDismissPage(results)) {
                    return false;
                }

                // Remove the page from the navigation stack along with any successive non-page views
                const wasActive: boolean = this.activePage === page;
                while (i + 1 < this.views.length && !(this.views[i + 1] instanceof Page)) {
                    this.deregisterView(this.views[i + 1]);
                }
                this.deregisterView(page);
                page.onInactive();

                // If the component we removed was active, notify the new active component
                if (wasActive) {
                    const activePage: Page = this.activePage;
                    if (activePage) {
                        activePage.onActive();
                    }
                }

                // Initiate destruction of the component.
                this.pageDismissed$.next(page.componentRef);

                // The page container destroys the component after it completes the close transition
                page.componentRef.onDestroy(() => {

                    // Notify the component instance that it has been dismissed
                    page.onDidDismissPage(results);

                    // Resolve or reject the promise returned from createPage
                    if (failure) {
                        page.viewPromiseReject(results);
                    } else {
                        page.viewPromiseResolve(results);
                    }
                });

                return true;
            }
        }

        return false;
    }

    private registerView(view: View): void {
        console.log("register: " + view.viewName);
        let index: number = this.views.indexOf(view);
        if (index != -1) {
            this.views.splice(index, 1);
        }
        this.views.push(view);
    }

    private deregisterView(view: View): void {
        console.log("deregister: " + view.viewName);
        let index = this.views.indexOf(view);
        if (index != -1) {
            this.views.splice(index, 1);
        }
    }

    public pageIsActive(page: Page): boolean {
        return this.activePage === page;
    }

    private get activePage(): Page {
        if (!this.views.length) {
            return undefined;
        }
        for (let i: number = this.views.length - 1; i >= 0; i--) {
            const view: View = this.views[i];
            if (view instanceof Page) {
                return view;
            }
        }
        return undefined;
    }
}



// WEBPACK FOOTER //
// ./src/app/orchestration/navigation.service.ts